/**
 * saleTicketCompactText.ts — Builder de texto plano COMPACTO del ticket de venta
 * (ahorro de papel). Es el mismo documento que el completo: misma tubería de
 * datos (cartItemsToTicketLines → descuentos → impuestos → fees de método de
 * pago), pero con layout condensado:
 *
 *  - Cabecera de negocio y meta en menos líneas (fecha+hora, caja/vendedor/sesión).
 *  - Productos: una línea principal `#I cant  nombre  total` + líneas de detalle
 *    `SKU | P.U | variantes` (en vez de bloques con rejillas `+---+`).
 *  - Menús/adiciones: una línea por opción (sin rejillas).
 *  - Totales y forma de pago condensados, conservando TODA la información.
 *
 * REGLA DE CAJA (invariante): NINGUNA línea puede exceder el ancho del papel
 * (48/32 columnas) y NINGÚN monto se trunca jamás — si la cantidad (ej. peso
 * «3.000kg») o el nombre no caben en su columna, el texto se envuelve y el
 * monto se imprime completo alineado a la derecha en la última línea.
 *
 * QUÉ EXPORTA: buildSaleTicketCompactPlainTextLines, saleTicketSupportsCompact
 * QUÉ NO DEBE HACER: no decide si se imprime (lo hace el builder completo según
 * `formatoTicket`); no imprime bytes.
 *
 * REGLA DE SEGURIDAD: solo se usa cuando `saleTicketSupportsCompact` lo permite.
 * Comprobantes con ajustes post-venta (devoluciones/anulaciones/saldo/puntos),
 * crédito o registro anulado SIEMPRE usan el formato completo para no perder
 * detalle forense.
 */

import type { SaleTicketEscPosInput, BusinessInfo } from "./saleTicketEscPos";
import {
  cartItemsToTicketLines,
  formatTicketSkuLine,
} from "./ticketLineBreakdown";
import { appendTaxLinesToTicket } from "./cartDiscountUtils";
import {
  appendTwoColWrapped,
  getTicketMaxWidth,
  padCol,
  padMoneyRight,
  sanitizeForThermal,
  wrapThermalText,
} from "./escposLayout";
import {
  blockHeaderTotal,
  groupTicketItemBlocks,
  resolveTicketBlockUnitPrice,
  splitTicketVariantSummary,
  ticketSubLineLineTotal,
  TICKET_UNIT_PRICE_LABEL,
} from "./ticketItemBlocks";
import type { TicketItem } from "@/features/caja/components/TicketItemsTable";
import {
  buildGlobalFooterLines,
  buildGlobalHeaderLines,
  buildGlobalNotaLines,
  buildGlobalPaymentLines,
  buildGlobalTotalsLines,
} from "./saleTicketGlobalSections";

/** Dinero COP local del ticket (mismo formato que el builder completo). */
function money(n: number): string {
  return `$${Math.round(n).toLocaleString("es-CO")}`;
}

interface CompactLayout {
  width: number;
  idxW: number;
  qtyW: number;
  moneyW: number;
  nameW: number;
  /** Columna donde arranca el nombre (después de #I + CANT). */
  indent: number;
}

/**
 * Columnas del detalle compacto: #I | CANT | nombre | TOTAL.
 * `idxW` es dinámico (dígitos del total de productos) y el presupuesto del
 * nombre siempre respeta el ancho exacto del papel.
 */
function compactLayout(width: number, idxW: number): CompactLayout {
  const moneyW = 12;
  const qtyW = 4;
  const nameW = Math.max(8, width - idxW - qtyW - moneyW - 3);
  return {
    width,
    idxW,
    qtyW,
    moneyW,
    nameW,
    indent: idxW + 1 + qtyW + 1,
  };
}

/** Regla fina de separación interna (guiones) — distinta de las de sección `_`. */
function pushThinRule(lines: string[], width: number): void {
  lines.push(sanitizeForThermal("-".repeat(width)));
}

function padRightCell(text: string, w: number): string {
  const t = (text || "").trim();
  if (t.length >= w) return t;
  return " ".repeat(w - t.length) + t;
}

/**
 * Fila principal de producto: `#I cant nombre …… total`.
 * Regla: el monto va SIEMPRE completo a la derecha. Si la cantidad (peso
 * «3.000kg») o el nombre no caben, se imprime en varias líneas y el monto
 * cierra la última línea del producto. Las palabras largas NUNCA se parten a
 * mitad en 58mm: las líneas de continuación usan el ancho completo del papel
 * (no solo la columna del nombre) para que la palabra entera quepa.
 */
function pushProductRow(
  lines: string[],
  lay: CompactLayout,
  idx: number | null,
  qty: string,
  nombre: string,
  total: string,
): void {
  const idxStr = idx == null ? "" : String(idx);
  const qtyStr = (qty || "").padEnd(lay.qtyW);
  const trimmedName = (nombre || "").trim();
  const qtyOver = qtyStr.trim().length > lay.qtyW;
  const nameEmpty = !trimmedName;

  // Caso simple (1 sola línea): #I cant nombre …… total — ancho exacto.
  if (!qtyOver && !nameEmpty && trimmedName.length <= lay.nameW) {
    lines.push(
      sanitizeForThermal(
        `${padRightCell(idxStr, lay.idxW)} ${qtyStr} ${trimmedName.padEnd(lay.nameW)} ${padMoneyRight(total, lay.moneyW)}`,
      ),
    );
    return;
  }
  if (nameEmpty && !qtyOver) {
    lines.push(
      sanitizeForThermal(
        `${padRightCell(idxStr, lay.idxW)} ${qtyStr} ${" ".padEnd(lay.nameW)} ${padMoneyRight(total, lay.moneyW)}`,
      ),
    );
    return;
  }

  // Ancho de continuación = todo el papel menos el sangrado: así una palabra
  // de 15+ caracteres entra entera y el wrap corta solo entre palabras.
  const contW = Math.max(
    lay.nameW,
    lay.moneyW > 0 ? lay.width - lay.indent : lay.nameW,
  );
  const parts = wrapThermalText(trimmedName || " ", contW);
  const indentStr = " ".repeat(lay.indent);
  const firstPrefix = `${padRightCell(idxStr, lay.idxW)} ${qtyStr} `;

  if (qtyOver || nameEmpty) {
    lines.push(sanitizeForThermal(firstPrefix.padEnd(lay.indent)));
  } else {
    lines.push(sanitizeForThermal(`${firstPrefix}${parts[0] ?? ""}`));
  }

  const rest = nameEmpty ? [] : qtyOver ? parts : parts.slice(1);

  const lastFitsWithMoney = () => {
    if (rest.length === 0) return false;
    const last = rest[rest.length - 1];
    return `${indentStr}${last}`.length + 1 + lay.moneyW <= lay.width;
  };

  for (let i = 0; i < rest.length; i++) {
    const part = rest[i];
    const isLast = i === rest.length - 1;
    if (isLast && lastFitsWithMoney()) {
      lines.push(
        sanitizeForThermal(
          `${indentStr}${part.padEnd(lay.nameW)} ${padMoneyRight(total, lay.moneyW)}`,
        ),
      );
    } else {
      lines.push(sanitizeForThermal(`${indentStr}${part}`));
    }
  }

  // Si el monto no pudo ir junto al último trozo (palabra casi del ancho del
  // papel), va en su propia línea alineado al borde derecho — nunca truncado.
  const moneyOnItsOwnLine = rest.length === 0 || !lastFitsWithMoney();
  if (moneyOnItsOwnLine) {
    lines.push(
      sanitizeForThermal(
        `${" ".repeat(Math.max(0, lay.width - total.length))}${total}`,
      ),
    );
  }
}

/** Cabecera de columnas del detalle compacto (`#I CANT DESCRIPCION TOTAL`). */
function pushColumnHeader(lines: string[], lay: CompactLayout): void {
  lines.push(
    sanitizeForThermal(
      `${padRightCell("#I", lay.idxW)} ${"CANT".padEnd(lay.qtyW)} ${"DESCRIPCION".padEnd(lay.nameW)} ${padMoneyRight("TOTAL", lay.moneyW)}`,
    ),
  );
}

/**
 * Línea de detalle condensada (SKU | P.U | variantes) con wrap inteligente.
 *
 * El texto llega con segmentos separados por ` | `. El wrap intenta NO partir
 * un segmento (ej. `Capacidad: 128 GB`) entre dos líneas: agrupa segmentos
 * completos por línea y solo corta dentro de uno cuando es demasiado largo
 * para una línea entera. Todas las líneas quedan indentadas por igual para
 * que nunca cuelguen palabras sueltas al borde izquierdo.
 */
function pushDetailLine(
  lines: string[],
  detail: string,
  width: number,
  indent: number,
): void {
  const text = detail.replace(/\s+/g, " ").trim();
  if (!text) return;
  const bodyW = Math.max(8, width - indent);
  const segments = text
    .split(" | ")
    .map((s) => s.trim())
    .filter(Boolean);

  const wrapSeg = (seg: string): string[] =>
    seg.length <= bodyW ? [seg] : wrapThermalText(seg, bodyW);

  let current = "";
  const flush = () => {
    if (current) {
      lines.push(sanitizeForThermal(`${" ".repeat(indent)}${current}`));
      current = "";
    }
  };

  for (const seg of segments) {
    const fitsNext = current
      ? current.length + 3 + seg.length <= bodyW
      : seg.length <= bodyW;
    if (!current) {
      // Primer segmento: si no cabe, lo partimos (puede ser muy largo).
      if (seg.length <= bodyW) {
        current = seg;
        continue;
      }
      for (const part of wrapSeg(seg)) {
        lines.push(sanitizeForThermal(`${" ".repeat(indent)}${part}`));
      }
      continue;
    }
    if (fitsNext) {
      current += " | " + seg;
      continue;
    }
    flush();
    current = seg;
  }
  flush();
}

/** Une partes de detalle con ` | ` (ignora vacías). */
function joinParts(parts: Array<string | undefined>): string {
  return parts.filter((p) => p?.trim()).join(" | ");
}

/** ¿La venta puede imprimirse en formato compacto sin perder detalle? */
export function saleTicketSupportsCompact(
  input: SaleTicketEscPosInput,
): boolean {
  if (input.anulada) return false;
  if ((input.devoluciones?.length ?? 0) > 0) return false;
  if ((input.anulaciones?.length ?? 0) > 0) return false;
  if (input.saldoVigente != null && input.saldoVigente !== input.total)
    return false;
  if (input.paymentMethod === "credito") return false;
  if (input.clientePuntosNombre || input.puntosGanados != null) return false;
  if (input.clienteCreditoNombre) return false;
  // La copia interna del dueño (tarifa/neto negocio) no se imprime en compacto.
  if (input.showNegocioFeeAudit) return false;
  return true;
}

/** Construye la lista de filas de ticket (misma fuente que el builder completo). */
function buildTicketLines(
  input: SaleTicketEscPosInput,
  defaultRatePct = 0,
): TicketItem[] {
  const ticketBreakdownOpts = input.catalogProducts?.length
    ? { catalog: input.catalogProducts }
    : undefined;
  const base = cartItemsToTicketLines(input.cart, ticketBreakdownOpts);
  // Sin descuentos por línea en el detalle: viven una sola vez en Totales.
  return appendTaxLinesToTicket(base, input.cart, defaultRatePct);
}

/** Bloque simple (producto normal + descuentos/impuestos de línea). */
function emitSimpleBlock(
  lines: string[],
  block: TicketItem[],
  lay: CompactLayout,
  width: number,
  idx: number | null,
): void {
  const header = block[0];
  if (!header) return;
  if (header.isGroupHeader) return;

  if (header.isDiscountLine || header.isArticleTotalLine || header.isTaxLine) {
    for (const row of block) {
      const amount = money(Math.abs(Math.round(row.lineSubtotal ?? 0)));
      const prefix = row.isDiscountLine ? "-" : "";
      appendTwoColWrapped(lines, `${prefix}${row.nombre}`, amount, width);
    }
    return;
  }

  const isPeso = !!header.peso && header.peso > 0;
  const qty = isPeso
    ? `${(header.peso ?? 0).toFixed(3)}kg`
    : String(header.cantidad ?? 1);
  const total = blockHeaderTotal(header);
  pushProductRow(lines, lay, idx, qty, header.nombre, money(total));

  const detailParts: string[] = [];
  if (header.skuResumen?.trim()) {
    detailParts.push(formatTicketSkuLine(header.skuResumen.trim()));
  }
  const unitPrice = resolveTicketBlockUnitPrice(header);
  if (!isPeso && unitPrice != null) {
    const unitLabel =
      (header.taxRatePct ?? 0) > 0
        ? `${TICKET_UNIT_PRICE_LABEL} (sin IVA)`
        : TICKET_UNIT_PRICE_LABEL;
    const qty = Math.max(1, header.cantidad || 1);
    detailParts.push(
      qty > 1
        ? `${unitLabel}: ${qty}x${money(unitPrice)}`
        : `${unitLabel}: ${money(unitPrice)}`,
    );
  }
  if (isPeso && (header.precioPorKilo ?? 0) > 0) {
    detailParts.push(`P.${"U"}: ${money(header.precioPorKilo ?? 0)}/kg`);
  }
  for (const v of splitTicketVariantSummary(header.variantesResumen ?? "")) {
    detailParts.push(v);
  }

  // Sublíneas con monto propio (IVA, precios unitarios y descuentos de la
  // línea, total de artículo o modificador pagado) van como fila propia
  // `- nombre ... monto`; las sub-líneas sin costo se unen al detalle.
  const pricedSublines: Array<{
    label: string;
    monto: number;
    plus?: boolean;
    unitQty?: number;
  }> = [];
  const freeSublines: string[] = [];
  const taxDivisor =
    (header.taxRatePct ?? 0) > 0 ? 1 + (header.taxRatePct ?? 0) / 100 : 1;
  const headerQty = Math.max(1, header.cantidad || 1);
  for (const sub of block.slice(1)) {
    if (sub.isGroupFooter || sub.isGroupHeader) continue;
    const monto = Math.round(sub.lineSubtotal ?? 0);
    const isPricedKind =
      sub.isDiscountLine || sub.isArticleTotalLine || sub.isTaxLine;
    if (sub.isSubLine || isPricedKind) {
      if (monto !== 0 || isPricedKind) {
        if (sub.isTaxLine) {
          pricedSublines.push({ label: sub.nombre, monto, plus: true });
        } else if (sub.esDescuentoPrecioLine) {
          const neto = Math.round(monto / taxDivisor);
          pricedSublines.push({
            label: sub.nombre,
            monto: neto,
            unitQty: neto > 0 && headerQty > 1 ? headerQty : undefined,
          });
        } else {
          pricedSublines.push({ label: sub.nombre, monto });
        }
      } else if (sub.nombre?.trim()) {
        freeSublines.push(sub.nombre.trim());
      }
      continue;
    }
    if (sub.nombre?.trim()) freeSublines.push(sub.nombre.trim());
  }

  pushDetailLine(
    lines,
    joinParts([...detailParts, ...freeSublines]),
    width,
    lay.indent,
  );
  for (const row of pricedSublines) {
    const base = money(Math.abs(row.monto));
    let amount = base;
    if (row.monto < 0) {
      amount = `-${base}`;
    } else if (row.plus) {
      amount = `+${base}`;
    } else if (row.unitQty && row.unitQty > 1) {
      amount = `${row.unitQty}x${base}`;
    }
    appendTwoColWrapped(lines, `- ${row.label}`, amount, width);
  }
}

/** Bloque expandido: menú (Base) o hub de adiciones — una línea por opción. */
function emitExpandedBlock(
  lines: string[],
  block: TicketItem[],
  lay: CompactLayout,
  width: number,
  idx: number | null,
): void {
  const header = block[0];
  if (!header) return;
  const headerName = header.nombre;
  // Los hubs de adiciones se listan con cantidad 1 (igual que hubs de menú).
  pushProductRow(
    lines,
    lay,
    idx,
    String(header.cantidad || 1),
    headerName,
    money(blockHeaderTotal(header)),
  );

  for (const it of block.slice(1)) {
    if (it.isGroupFooter) {
      const footerText = (it.opcionesResumen ?? it.variantesResumen)?.trim();
      if (!footerText) continue;
      if (it.footerKind === "orderCustomization") {
        pushDetailLine(lines, `[${footerText}]`, width, lay.indent);
      } else {
        for (const part of splitTicketVariantSummary(footerText)) {
          pushDetailLine(lines, part, width, lay.indent);
        }
      }
      continue;
    }
    if (it.isDiscountLine || it.isArticleTotalLine || it.isSubLine) {
      const lineTotal = ticketSubLineLineTotal(it);
      if (lineTotal !== 0) {
        appendTwoColWrapped(
          lines,
          `- ${it.nombre}`,
          `${lineTotal < 0 ? "-" : ""}${money(Math.abs(lineTotal))}`,
          width,
        );
      } else {
        pushDetailLine(lines, `- ${it.nombre}`, width, lay.indent);
      }
      continue;
    }
    if (it.isTaxLine) {
      pushDetailLine(lines, it.nombre, width, lay.indent);
      continue;
    }
    if (it.nombre) {
      pushDetailLine(lines, it.nombre, width, lay.indent);
    }
  }

  // El total del bloque ya se mostró en la fila cabecera (blockHeaderTotal).
}

/**
 * Líneas de texto plano del ticket COMPACTO (58/80 mm).
 * Conserva la misma información que `buildSaleTicketPlainTextLines` pero con
 * menos líneas de papel: sin rejillas `+---+`, meta compacta y filas unidas.
 * Las zonas globales (cabecera/meta, totales, pago/nota) salen del módulo
 * compartido `saleTicketGlobalSections` para que los 3 formatos sean idénticos;
 * aquí solo vive el DETALLE de productos condensado.
 */
export function buildSaleTicketCompactPlainTextLines(
  input: SaleTicketEscPosInput,
  biz: BusinessInfo,
): string[] {
  const lines: string[] = [];
  const maxW = getTicketMaxWidth(input.paperWidthMm);
  const emit = (s: string) => lines.push(sanitizeForThermal(s));
  const emitCenter = (s: string) => emit(padCol(s, maxW, "center"));

  const ticketLines = buildTicketLines(input, biz.impuestoDefaultPct ?? 0);
  const blocks = groupTicketItemBlocks(ticketLines);

  // Enumeración #I como en Ultra: ancho según el total de productos.
  const productCount = blocks.filter((b) => {
    const f = b[0];
    return Boolean(
      f &&
      !f.isDiscountLine &&
      !f.isArticleTotalLine &&
      !f.isTaxLine &&
      (f.isGroupHeader || true),
    );
  }).length;
  const idxW = Math.max(2, Math.min(3, String(productCount).length));
  const lay = compactLayout(maxW, idxW);

  lines.push(...buildGlobalHeaderLines(input, biz));
  pushThinRule(lines, maxW);
  emitCenter("DETALLE DE VENTA");
  pushThinRule(lines, maxW);
  pushColumnHeader(lines, lay);

  let index = 0;
  for (let bi = 0; bi < blocks.length; bi++) {
    const block = blocks[bi];
    const first = block[0];
    if (!first) continue;
    if (bi > 0) pushThinRule(lines, maxW);
    if (first.isDiscountLine || first.isArticleTotalLine || first.isTaxLine) {
      // Fila suelta (impuesto/descuento global sin producto): sin número.
      if (first.isGroupHeader) {
        emitExpandedBlock(lines, block, lay, maxW, null);
      } else {
        emitSimpleBlock(lines, block, lay, maxW, null);
      }
      continue;
    }
    index += 1;
    if (first.isGroupHeader) {
      emitExpandedBlock(lines, block, lay, maxW, index);
    } else {
      emitSimpleBlock(lines, block, lay, maxW, index);
    }
  }
  pushThinRule(lines, maxW);

  lines.push(...buildGlobalTotalsLines(input));
  lines.push(...buildGlobalPaymentLines(input));
  lines.push(...buildGlobalNotaLines(input));
  pushThinRule(lines, maxW);

  // ── Pie oficial compartido (sin duplicados ni «Imagen no editable») ──────
  lines.push(...buildGlobalFooterLines(biz, maxW));
  lines.push("");
  lines.push("");

  return lines;
}
